// ============================================================================
//  GoalCard.jsx — one goal, collapsed to a summary or expanded to full detail.
//
//  Photo URLs are signed lazily on expand rather than at load time; see
//  storage.signPhotoUrls for why.
// ============================================================================

import React, { useState, useEffect, useRef } from "react";
import {
  Flag, AlertTriangle, Edit3, X, Compass, Camera, Lock, ChevronRight, Check,
} from "lucide-react";
import {
  C, FS, RAG, display, kicker, overlay, smallBtn, smallGhost, iconBtnSm,
} from "../theme/tokens.js";
import { Bar, RagPill } from "../theme/primitives.jsx";
import { monthLabel } from "../lib/metrics.js";
import { signPhotoUrls } from "../lib/storage.js";

/**
 * One month of history. Collapsed it is month + status + percentage; expanded
 * it shows the note, blockers and commitment for that month.
 *
 * Those three fields used to be dropped entirely for every past month — even
 * for the man who wrote them — so only the newest entry was ever readable.
 * With teammates now reading each other, the history is the record, so it has
 * to be legible.
 */
function HistoryRow({ h, first }) {
  const [open, setOpen] = useState(false);
  const detail = [
    ["Update", h.note],
    ["Blockers", h.blockers],
    ["Committed to", h.commitment],
  ].filter(([, v]) => v && String(v).trim());

  return (
    <div style={{ borderTop: first ? "none" : `1px dotted ${C.line}` }}>
      <button
        onClick={() => detail.length && setOpen((v) => !v)}
        disabled={!detail.length}
        style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
          width: "100%", padding: "7px 0", background: "transparent", border: "none",
          fontSize: 12.5, color: C.sub, textAlign: "left",
          cursor: detail.length ? "pointer" : "default",
          fontFamily: "'Barlow', sans-serif" }}>
        <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {detail.length > 0 && (
            <ChevronRight size={12}
              style={{ transform: open ? "rotate(90deg)" : "none", transition: "transform 140ms" }} />
          )}
          {monthLabel(h.month)}
          {h.commitment_kept === true && <Check size={12} color="#1F6B40" />}
        </span>
        <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <RagPill rag={h.rag} />
          <b style={{ ...display, color: RAG[h.rag].dot }}>{h.pct}%</b>
        </span>
      </button>

      {open && (
        <div style={{ display: "grid", gap: 7, padding: "2px 0 12px 18px" }}>
          {detail.map(([k, v]) => (
            <div key={k} style={{ fontSize: 12.5, lineHeight: 1.55 }}>
              <span style={{ fontWeight: 700, color: k === "Blockers" ? C.redDk : C.text }}>
                {k}:
              </span>{" "}
              <span style={{ color: C.sub }}>{v}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function GoalCard({ goal, mark, history, onMark, onEdit, onDelete, onAddPhoto, onDeletePhoto, readOnly }) {
  const [open, setOpen] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [photoErr, setPhotoErr] = useState("");
  const [signed, setSigned] = useState({});   // storage_path -> signed URL
  const photoRef = useRef();
  const pct = mark?.pct ?? 0;
  const rag = mark?.rag ?? "green";
  const color = RAG[rag].dot;
  const rawPhotos = goal.photos || [];

  // Sign the gallery only once the card is actually expanded. loadState hands
  // back paths without URLs precisely so a big roster does not sign hundreds of
  // photos nobody looks at. A freshly uploaded photo already carries its own
  // dataUrl, so prefer that and only sign what is missing one.
  const needSigning = rawPhotos.filter((p) => !p.dataUrl && p.storage_path).map((p) => p.storage_path);
  const needKey = needSigning.join("|");
  useEffect(() => {
    if (!open || !needKey) return;
    let cancelled = false;
    signPhotoUrls(needKey.split("|"))
      .then((map) => { if (!cancelled) setSigned((prev) => ({ ...prev, ...map })); })
      .catch(() => { if (!cancelled) setPhotoErr("Could not load photos. Try reopening this goal."); });
    return () => { cancelled = true; };
  }, [open, needKey]);

  const photos = rawPhotos.map((p) => ({
    ...p,
    dataUrl: p.dataUrl || signed[p.storage_path] || "",
  }));

  const pickPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) {
      setPhotoErr("Image is larger than 15MB — pick a smaller one.");
      return;
    }
    try {
      setPhotoErr("");
      await onAddPhoto(goal.id, file);   // hands the raw File to Supabase Storage
    } catch (err) {
      setPhotoErr(err.message || "Upload failed.");
    }
  };

  return (
    <div style={{ background: C.panel, border: `1px solid ${C.line}`, borderRadius: 14,
      padding: "18px 20px", borderLeft: `3px solid ${color}`, boxShadow: "0 1px 2px rgba(19,34,41,0.04)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 14 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          {(() => {
            const fDef = FS.find((x) => x.key === goal.f);
            if (!fDef) return null;
            const FIcon = fDef.icon;
            return (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6,
                background: C.redSoft, color: C.redDk, padding: "3px 10px 3px 8px",
                borderRadius: 99, fontSize: 10.5, fontWeight: 700, letterSpacing: ".08em",
                textTransform: "uppercase", border: `1px solid ${C.redLine}`, marginBottom: 9 }}>
                <FIcon size={12} /> {fDef.label}
              </span>
            );
          })()}
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <h4 style={{ ...display, fontSize: 17, margin: 0, color: C.text }}>{goal.title}</h4>
            <RagPill rag={rag} />
            {goal.private_details && (
              <span
                title={readOnly
                  ? "This man keeps his updates on this goal private to his Team Admin."
                  : "Your notes, blockers and photos on this goal are hidden from your teammates."}
                style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 10.5,
                  fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase",
                  padding: "3px 9px 3px 7px", borderRadius: 99, background: "#E9ECEE",
                  color: C.sub, whiteSpace: "nowrap" }}>
                <Lock size={11} /> Private
              </span>
            )}
          </div>
          {mark?.note && <p style={{ margin: "8px 0 0", fontSize: 13.5, color: C.sub, lineHeight: 1.55 }}>{mark.note}</p>}
        </div>
        <div style={{ textAlign: "right", flexShrink: 0 }}>
          <div style={{ ...display, fontSize: 26, color, lineHeight: 1 }}>{pct}<span style={{ fontSize: 14 }}>%</span></div>
          {mark && <div style={{ fontSize: 10, color: C.faint, marginTop: 4, letterSpacing: ".04em" }}>{monthLabel(mark.month)}</div>}
        </div>
      </div>

      <div style={{ marginTop: 14 }}><Bar value={pct} color={color} /></div>

      {(mark?.blockers || mark?.commitment) && (
        <div style={{ marginTop: 14, display: "grid", gap: 8 }}>
          {mark.blockers && (
            <div style={{ fontSize: 12.5, color: C.redDk, display: "flex", gap: 7 }}>
              <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
              <span><b style={{ fontWeight: 700 }}>Blockers:</b> {mark.blockers}</span>
            </div>
          )}
          {mark.commitment && (
            <div style={{ fontSize: 12.5, color: "#1F6B40", display: "flex", gap: 7 }}>
              <Flag size={14} style={{ flexShrink: 0, marginTop: 1 }} />
              <span><b style={{ fontWeight: 700 }}>Next month:</b> {mark.commitment}</span>
            </div>
          )}
        </div>
      )}

      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 16 }}>
        {!readOnly && (
          <button onClick={onMark} style={smallBtn}><Compass size={14} /> Log Mile Marker</button>
        )}
        <button onClick={() => setOpen(!open)} style={smallGhost}>
          {open ? "Hide details" : `Details${history.length ? ` · ${history.length}` : ""}${photos.length ? ` · ${photos.length} 📷` : ""}`}
        </button>
        {!readOnly && (
          <div style={{ marginLeft: "auto", display: "flex", gap: 4 }}>
            <button onClick={onEdit} style={iconBtnSm}><Edit3 size={14} /></button>
            <button onClick={onDelete} style={iconBtnSm}><X size={14} /></button>
          </div>
        )}
      </div>

      {open && (
        <div style={{ marginTop: 14, borderTop: `1px solid ${C.line}`, paddingTop: 12 }}>
          <div style={{ display: "grid", gap: 4, marginBottom: 14 }}>
            {[["S", goal.smart?.s], ["M", goal.smart?.m], ["A", goal.smart?.a], ["R", goal.smart?.r], ["T", goal.smart?.t]]
              .filter(([, v]) => v).map(([k, v]) => (
                <div key={k} style={{ fontSize: 12, color: C.sub, display: "flex", gap: 8 }}>
                  <span style={{ ...display, color: C.red, width: 14 }}>{k}</span>{v}
                </div>
              ))}
          </div>
          {history.slice().reverse().map((h, i) => (
            <HistoryRow key={i} h={h} first={i === 0} />
          ))}

          {/* photo gallery */}
          <div style={{ marginTop: 16, borderTop: `1px solid ${C.line}`, paddingTop: 14 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
              <span style={{ ...kicker }}>Progress photos {photos.length ? `· ${photos.length}` : ""}</span>
              {!readOnly && (
                <button onClick={() => photoRef.current?.click()} style={smallBtn}>
                  <Camera size={13} /> Add photo
                </button>
              )}
              <input ref={photoRef} type="file" accept="image/*" capture="environment"
                onChange={pickPhoto} style={{ display: "none" }} />
            </div>
            {photoErr && <div style={{ fontSize: 12, color: C.redDk, marginBottom: 8 }}>{photoErr}</div>}
            {photos.length === 0 ? (
              <div style={{ fontSize: 12.5, color: C.faint }}>
                No photos yet{!readOnly && " — add a scale shot, a screenshot, anything that shows the proof."}
              </div>
            ) : (
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                {photos.slice().sort((a, b) => b.ts - a.ts).map((ph) => (
                  <div key={ph.id} style={{ position: "relative" }}>
                    <img src={ph.dataUrl} alt="" loading="lazy" onClick={() => setLightbox(ph)}
                      style={{ width: 86, height: 86, objectFit: "cover", borderRadius: 10,
                        border: `1px solid ${C.line}`, cursor: "zoom-in", display: "block",
                        background: C.shell }} />
                    <div style={{ fontSize: 9.5, color: C.faint, marginTop: 4, textAlign: "center",
                      letterSpacing: ".02em" }}>
                      {new Date(ph.ts).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "2-digit" })}
                    </div>
                    {!readOnly && (
                      <button onClick={() => onDeletePhoto(goal.id, ph.id)} style={{
                        position: "absolute", top: -7, right: -7, width: 20, height: 20, borderRadius: 99,
                        border: "none", background: C.red, color: "#fff", cursor: "pointer",
                        fontSize: 11, lineHeight: 1, display: "grid", placeItems: "center" }}>×</button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {lightbox && (
        <div onClick={() => setLightbox(null)} style={{ ...overlay, cursor: "zoom-out" }}>
          <div onClick={(e) => e.stopPropagation()} style={{ maxWidth: "90vw", maxHeight: "90vh",
            display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
            <img src={lightbox.dataUrl} alt="" style={{ maxWidth: "90vw", maxHeight: "78vh",
              objectFit: "contain", borderRadius: 12, boxShadow: "0 20px 60px rgba(0,0,0,0.5)" }} />
            <div style={{ display: "flex", alignItems: "center", gap: 16, color: "#fff" }}>
              <span style={{ fontSize: 13 }}>
                {new Date(lightbox.ts).toLocaleDateString("en-US", { weekday: "short", month: "long", day: "numeric", year: "numeric" })}
              </span>
              <button onClick={() => setLightbox(null)} style={{ ...smallGhost, color: "#fff" }}>
                <X size={14} /> Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  DASHBOARD                                                          */
/* ------------------------------------------------------------------ */
